import { useEffect, useState, useMemo } from 'react';

import { addError } from '@shared/utils/logger';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { JoyHotelAccommodationListingDTO } from '../AccommodationsConsole.types';
import { isValidProperty, transformPropertiesToListings } from '../AccommodationsConsole.utils';
import { useAccommodationsConsolePropertiesV2Query, FetchStrategy, SearchExtendedAccommodationPropertiesV2Payload } from '@graphql/generated';

interface UseBackgroundPricingRefreshArgs
  extends Readonly<{
    isFetchingListings: boolean;
    regionalHotelsPayload: SearchExtendedAccommodationPropertiesV2Payload;
  }> {}

interface BackgroundPricingRefreshResult
  extends Readonly<{
    isLoading: boolean;
    error: Error | undefined;
    listings: ReadonlyArray<JoyHotelAccommodationListingDTO>;
  }> {}

export const useBackgroundPricingRefresh = (args: UseBackgroundPricingRefreshArgs): BackgroundPricingRefreshResult => {
  const { regionalHotelsPayload, isFetchingListings } = args;

  const [isFeatureEnabled] = useIsFeatureEnabled('accommodationsBatchPricingRefresh');
  const [listings, setListings] = useState<readonly JoyHotelAccommodationListingDTO[]>([]);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  const payload = useMemo(() => {
    if (!regionalHotelsPayload?.customSearchParams) {
      return null;
    }

    return {
      eventHandle: regionalHotelsPayload.eventHandle,
      customSearchParams: {
        ...regionalHotelsPayload.customSearchParams,
        radiusKM: 5,
        useRealtimePricing: true,
        fetchStrategy: FetchStrategy.PREFER_VENDOR,
      },
    };
  }, [regionalHotelsPayload]);

  const shouldSkipQuery = !isFeatureEnabled || !payload || isFetchingListings;

  useEffect(() => {
    setListings([]);
  }, [regionalHotelsPayload]);

  const { data, error, loading } = useAccommodationsConsolePropertiesV2Query({
    batchMode: 'off',
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
    variables: { payload: payload! },
  });

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (!properties) {
      return;
    }

    const validProperties = properties.filter(isValidProperty);
    const newListings = transformPropertiesToListings(validProperties, mapHotelRoomPrice);
    setListings(newListings);
  }, [data, mapHotelRoomPrice]);

  useEffect(() => {
    if (error) {
      addError(error, {
        errorMessage: error.message,
        context: 'useBackgroundPricingRefresh',
        source: 'Background pricing refresh failed (AccommodationsConsole)',
      });
    }
  }, [error]);

  return {
    error,
    listings,
    isLoading: loading,
  };
};
